// Sweet Match - the running game: what is on the field, what is in the pot, and
// the match / win / fail rules. No DOM.
//
// engine.ts owns the static side (geometry + level generation), physics.ts the
// solver. This module is the only place that knows a candy has a *type* and that
// two of a kind cancel out.
//
// The view drives it with tapAt() + advanceState() and reacts to the returned
// events; it never re-implements a rule. That split is what lets the headless
// winnability bot in the validator play exactly the same game as the player.

import {
  POT_CAPACITY,
  breakJams,
  buildLevelWorld,
  generateSmLevel,
  inZone,
  limitLineY,
  potZone,
  type SmLevel,
} from "./engine";
import { CANDY_R } from "./engine";
import { advance, removeBodies, speed, type Body, type World } from "./physics";

/** Grace period once the queue backs up past the fail line. */
export const LOSE_GRACE_MS = 1500;
/**
 * A dropped candy slower than this (units per step) is not in transit any more.
 * Sliding down the funnel runs at 0.03-0.06, so this cleanly separates "stuck on
 * top of the queue" from "on its way down".
 */
const OVERFLOW_SLOW = 0.01;
/**
 * How fast the timer unwinds when the queue is not backed up, relative to how
 * fast it fills. A churning pile flickers in and out of the overflow condition
 * frame by frame; a hard reset would mean it never actually loses. Decaying
 * instead of resetting makes the timer measure *sustained* trouble, while still
 * letting a genuine save (a match that drops the stack) clear it quickly.
 */
const TIMER_DECAY = 2;
/** Warning only shows once trouble has persisted, so it cannot strobe. */
const WARN_AT_MS = 250;
/**
 * Everything must have stood still this long before a stalemate is declared,
 * so a pair that is still tumbling toward each other gets its chance to meet.
 * Comfortably above the jam breaker's 400 ms threshold: a candy the nudge is
 * still working on keeps moving and so keeps resetting its settledMs.
 */
const STUCK_SETTLED_MS = 600;

export interface Candy {
  /** Same id as the physics body. */
  id: number;
  type: number;
  /** Drawn as "?" until tapped. The type underneath is already fixed. */
  mystery: boolean;
  revealed: boolean;
  /** Encased in ice: the first tap breaks it, the second drops the candy. */
  frozen: boolean;
  /** Tapped and falling (mirrors body.dynamic, kept for readability). */
  dropped: boolean;
}

export type SmStatus = "playing" | "won" | "lost";

export interface SmState {
  level: SmLevel;
  world: World;
  candies: Map<number, Candy>;
  /** Candies still on the field (field + queue + pot). */
  remaining: number;
  /** Unmatched candies in the queue right now - the pressure the player manages. */
  pressure: number;
  /** Highest pressure seen this attempt. This is what stars are scored on. */
  peak: number;
  /** Counts up while the queue is over the line; resets when it drains. */
  loseTimerMs: number;
  warning: boolean;
  status: SmStatus;
  tick: number;
}

export type SmEvent =
  | { kind: "match"; a: Candy; b: Candy; x: number; y: number }
  /** A candy was released. Emitted so the view can tell a hit from a miss - both
   *  would otherwise come back as "no events". */
  | { kind: "drop"; candy: Candy }
  | { kind: "ice"; candy: Candy }
  | { kind: "reveal"; candy: Candy }
  | { kind: "warn"; on: boolean }
  | { kind: "won" }
  /** "overflow" = the queue backed up past the fail line; "stuck" = stalemate:
   *  nothing left to tap and no touching pair - the stack is permanently dead. */
  | { kind: "lost"; reason: "overflow" | "stuck" };

export function createState(n: number): SmState {
  const level = generateSmLevel(n);
  const world = buildLevelWorld(level);
  const candies = new Map<number, Candy>();
  level.spawn.forEach((s, i) => {
    candies.set(i + 1, {
      id: i + 1,
      type: s.type,
      mystery: s.mystery,
      revealed: !s.mystery,
      frozen: s.frozen,
      dropped: false,
    });
  });
  return {
    level,
    world,
    candies,
    remaining: level.spawn.length,
    pressure: 0,
    peak: 0,
    loseTimerMs: 0,
    warning: false,
    status: "playing",
    tick: 0,
  };
}

/** Stars from stack pressure. Lower is better: it means you planned ahead. */
export function starsFor(peak: number, peakPar: number): number {
  if (peak <= peakPar) return 3;
  if (peak <= peakPar + 1) return 2;
  return 1;
}

/**
 * Tap at a point in field units. Returns the events it caused (breaking ice,
 * revealing a mystery candy) or an empty array if nothing was hit.
 *
 * Topmost candy wins when several overlap the touch, matching what the player
 * sees: the one drawn on top is the one closest to the front of the pile.
 */
export function tapAt(state: SmState, x: number, y: number): SmEvent[] {
  if (state.status !== "playing") return [];
  const hits = state.world.bodies
    .filter((b) => !b.dynamic && Math.hypot(b.x - x, b.y - y) <= CANDY_R)
    .sort((a, b) => a.y - b.y);
  const body = hits[0];
  if (!body) return [];
  const candy = state.candies.get(body.id);
  if (!candy) return [];

  const events: SmEvent[] = [];
  if (candy.frozen) {
    // Ice takes the first tap; the candy stays put.
    candy.frozen = false;
    events.push({ kind: "ice", candy });
    return events;
  }
  if (!candy.revealed) {
    candy.revealed = true;
    events.push({ kind: "reveal", candy });
  }
  candy.dropped = true;
  body.dynamic = true;
  events.push({ kind: "drop", candy });
  return events;
}

/** Candies sitting in the queue (neck + pot), lowest first. */
function queue(state: SmState): Array<{ candy: Candy; body: Body }> {
  const zone = potZone();
  const out: Array<{ candy: Candy; body: Body }> = [];
  for (const body of state.world.bodies) {
    if (!body.dynamic || !inZone(body.x, body.y, zone)) continue;
    const candy = state.candies.get(body.id);
    if (candy) out.push({ candy, body });
  }
  return out.sort((a, b) => b.body.y - a.body.y);
}

/**
 * How close two centers must be to count as "touching". Resting contact sits at
 * exactly 2*CANDY_R (the solver resolves overlap away); the slack covers Verlet
 * jitter and a visually-touching sliver of air. Safe because the single-file
 * column keeps non-neighbours >= ~1.4 units apart - see the pot-width comment
 * in engine.ts.
 */
const TOUCH_DIST = 2 * CANDY_R + 0.08;

/**
 * Two of a kind that *touch* in the queue, lowest pair first.
 *
 * This is the rule that makes order the whole game: a pair does not pop for
 * merely sharing the pot, the two candies have to physically meet. In the
 * single-file column that means a new arrival only ever pops against the top
 * of the stack, so a partner buried under a different type is dead weight
 * until everything above it clears.
 *
 * One of the two may still be *above* the zone: when the queue is backed up
 * past the fail line, the save the player is owed is "drop the partner of the
 * top candy" - that partner lands on the stack without ever entering the zone,
 * so requiring both in-zone would turn correct play into a loss. Requiring at
 * least one in-zone still keeps mid-air contacts up in the funnel cone from
 * matching.
 */
function findMatch(state: SmState): [{ candy: Candy; body: Body }, { candy: Candy; body: Body }] | null {
  const zone = potZone();
  const dropped: Array<{ candy: Candy; body: Body }> = [];
  for (const body of state.world.bodies) {
    if (!body.dynamic) continue;
    const candy = state.candies.get(body.id);
    if (candy) dropped.push({ candy, body });
  }
  dropped.sort((a, b) => b.body.y - a.body.y); // lowest first
  for (let i = 0; i < dropped.length; i++) {
    const a = dropped[i];
    for (let j = i + 1; j < dropped.length; j++) {
      const b = dropped[j];
      if (a.candy.type !== b.candy.type) continue;
      if (!inZone(a.body.x, a.body.y, zone) && !inZone(b.body.x, b.body.y, zone)) continue;
      if (Math.hypot(a.body.x - b.body.x, a.body.y - b.body.y) > TOUCH_DIST) continue;
      return [a, b];
    }
  }
  return null;
}

/**
 * Is the queue backing up past the fail line?
 *
 * Two conditions, and both matter:
 *
 * 1. **A dropped candy is stalled above the line.** Only *dropped* candies
 *    count - an untapped one floats above the line by definition, so counting
 *    those would fail every level on the first frame. This is the single easiest
 *    bug to write in this file.
 * 2. **The queue is actually full.** Without this the rule fights the jam
 *    breaker: a candy bridging the neck above a nearly empty pot is a jam, which
 *    engine.breakJams() is there to shake loose, not something the player did
 *    wrong. And because the fail timer starts sooner (250 ms) than the nudge is
 *    allowed to act (400 ms), the loss would land while the safety net was still
 *    winding up. Both rules now read the same "is there room?" signal, so they
 *    are complementary instead of contradictory.
 */
function overflowing(state: SmState): boolean {
  if (state.pressure < POT_CAPACITY) return false;
  const limit = limitLineY();
  return state.world.bodies.some((b) => b.dynamic && b.y < limit && speed(b) < OVERFLOW_SLOW);
}

/**
 * Step the world and apply the rules. Returns what happened, for the view to
 * animate and play sounds against.
 */
export function advanceState(state: SmState, dtMs: number): SmEvent[] {
  const events: SmEvent[] = [];
  if (state.status !== "playing") return events;

  advance(state.world, dtMs);
  // Safety net against a granular bridge; self-limiting, and gated on the pot
  // having room so it can never rescue a real overflow. See engine.breakJams.
  breakJams(state.world, state.tick++);

  // Resolve every match this frame: three of a kind can arrive together, and
  // bodies are removed immediately so the popping pair cannot be matched twice
  // or keep colliding while the view animates it.
  for (;;) {
    const pair = findMatch(state);
    if (!pair) break;
    const [a, b] = pair;
    removeBodies(state.world, new Set([a.body.id, b.body.id]));
    state.candies.delete(a.candy.id);
    state.candies.delete(b.candy.id);
    state.remaining -= 2;
    events.push({
      kind: "match",
      a: a.candy,
      b: b.candy,
      x: (a.body.x + b.body.x) / 2,
      y: (a.body.y + b.body.y) / 2,
    });
  }

  state.pressure = queue(state).length;
  state.peak = Math.max(state.peak, state.pressure);

  if (state.remaining <= 0) {
    state.status = "won";
    if (state.warning) {
      state.warning = false;
      events.push({ kind: "warn", on: false });
    }
    events.push({ kind: "won" });
    return events;
  }

  if (overflowing(state)) {
    state.loseTimerMs += dtMs;
  } else if (
    // Stalemate: nothing left to tap, everything at rest, and the match loop
    // above found no touching pair. Under touch-matching a stack like a,b,a,b
    // is permanently dead - without this rule the game would sit in "playing"
    // forever with no move available. Gated behind !overflowing so a genuine
    // backup keeps its own message and grace period.
    state.world.bodies.every((b) => b.dynamic && b.settledMs >= STUCK_SETTLED_MS)
  ) {
    state.status = "lost";
    events.push({ kind: "lost", reason: "stuck" });
    return events;
  } else {
    // Unwind rather than reset - see TIMER_DECAY.
    state.loseTimerMs = Math.max(0, state.loseTimerMs - dtMs * TIMER_DECAY);
  }

  const shouldWarn = state.loseTimerMs >= WARN_AT_MS;
  if (shouldWarn !== state.warning) {
    state.warning = shouldWarn;
    events.push({ kind: "warn", on: shouldWarn });
  }
  if (state.loseTimerMs >= LOSE_GRACE_MS) {
    state.status = "lost";
    events.push({ kind: "lost", reason: "overflow" });
  }

  return events;
}

/** Candies still waiting for a tap, lowest on screen first. */
export function idleCandies(state: SmState): Array<{ candy: Candy; body: Body }> {
  const out: Array<{ candy: Candy; body: Body }> = [];
  for (const body of state.world.bodies) {
    if (body.dynamic) continue;
    const candy = state.candies.get(body.id);
    if (candy) out.push({ candy, body });
  }
  return out.sort((a, b) => b.body.y - a.body.y);
}

/** Types sitting unmatched in the queue - what a planning player watches. */
export function queuedTypes(state: SmState): number[] {
  return queue(state).map((e) => e.candy.type);
}

/** True while the queue still has room by the conservative capacity bound. */
export function queueHasRoom(state: SmState): boolean {
  return state.pressure < POT_CAPACITY;
}
